import { Activity, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  useChildFitnessHistory,
  useChildClubBenchmarks,
  useChildAgeMatchedBenchmarks,
} from '@/hooks/use-parent-data';
import { FITNESS_TEST_META } from '@/shared/fitnessTestMeta';

interface FitnessTabProps {
  childId: string;
  dateOfBirth: string | null;
}

// ── Trend arrow ────────────────────────────────────────────────────────────

function TrendIcon({
  current,
  previous,
  higherIsBetter,
}: {
  current: number;
  previous: number | undefined;
  higherIsBetter: boolean;
}) {
  if (previous === undefined || current === previous) {
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  }
  const improved = higherIsBetter ? current > previous : current < previous;
  return improved ? (
    <TrendingUp className="w-4 h-4 text-green-500" />
  ) : (
    <TrendingDown className="w-4 h-4 text-red-500" />
  );
}

// ── Percentile bar ─────────────────────────────────────────────────────────

function PercentileBar({ rank, total }: { rank: number; total: number }) {
  const percentile = Math.round(((total - rank + 1) / total) * 100);
  return (
    <div className="flex items-center gap-2 mt-1">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-green-500 rounded-full transition-all"
          style={{ width: `${percentile}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground tabular-nums shrink-0">
        Top {percentile}%
      </span>
    </div>
  );
}

// ── Per-test card ──────────────────────────────────────────────────────────

interface TestCardProps {
  testType: string;
  history: Array<{ value: number; date: string; unit: string }>;
  benchmark: {
    clubBest: number;
    clubAvg: number;
    clubPlayerCount: number;
    childRank: number | null;
    higherIsBetter: boolean;
    unit: string;
    label: string;
  };
  ageBestValue?: number | null;
  ageMatchedAge?: number | null;
}

function TestCard({ testType, history, benchmark, ageBestValue, ageMatchedAge }: TestCardProps) {
  const meta = FITNESS_TEST_META[testType];
  const label = meta?.label ?? benchmark.label ?? testType;
  const unit = benchmark.unit || meta?.unit || '';
  const { higherIsBetter } = benchmark;

  const sorted = [...history].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sorted[sorted.length - 1];
  const previous = sorted[sorted.length - 2];

  if (!latest) return null;

  const formattedDate = new Date(latest.date).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span>{label}</span>
          <TrendIcon
            current={latest.value}
            previous={previous?.value}
            higherIsBetter={higherIsBetter}
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Latest result */}
        <div className="flex items-end gap-2">
          <span className="text-3xl font-bold text-foreground tabular-nums">
            {latest.value}
          </span>
          <span className="text-sm text-muted-foreground pb-1">{unit}</span>
          <span className="text-xs text-muted-foreground pb-1 ml-auto">{formattedDate}</span>
        </div>

        {/* Club rank */}
        {benchmark.childRank !== null && (
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Club rank</span>
              <span className="font-medium">
                {benchmark.childRank}{ordinal(benchmark.childRank)} of {benchmark.clubPlayerCount}
              </span>
            </div>
            <PercentileBar rank={benchmark.childRank} total={benchmark.clubPlayerCount} />
          </div>
        )}

        {/* Club best & avg */}
        <div className="grid grid-cols-2 gap-3 pt-1 border-t">
          <div>
            <p className="text-xs text-muted-foreground">Club best</p>
            <p className="text-sm font-semibold tabular-nums">
              {benchmark.clubBest} <span className="font-normal text-muted-foreground">{unit}</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Club average</p>
            <p className="text-sm font-semibold tabular-nums">
              {benchmark.clubAvg} <span className="font-normal text-muted-foreground">{unit}</span>
            </p>
          </div>
        </div>

        {/* Age-matched benchmark */}
        {ageBestValue != null && ageMatchedAge != null && (
          <div className="pt-1 border-t">
            <p className="text-xs text-muted-foreground">
              Best at age {ageMatchedAge} (±1yr) in club history
            </p>
            <p className="text-sm font-semibold tabular-nums">
              {ageBestValue} <span className="font-normal text-muted-foreground">{unit}</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] ?? s[v] ?? s[0];
}

// ── Main FitnessTab ────────────────────────────────────────────────────────

export function FitnessTab({ childId, dateOfBirth }: FitnessTabProps) {
  const { data: history, isLoading: historyLoading } = useChildFitnessHistory(childId);
  const { data: benchmarks, isLoading: benchmarksLoading } = useChildClubBenchmarks(childId);
  const { data: ageData, isLoading: ageLoading } = useChildAgeMatchedBenchmarks(childId);

  const isLoading = historyLoading || benchmarksLoading || ageLoading;

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    );
  }

  if (!benchmarks || benchmarks.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="font-medium">No fitness results yet</p>
        <p className="text-sm mt-1">
          Results will appear here once the coach records fitness tests.
        </p>
        {!dateOfBirth && (
          <p className="text-sm mt-3 text-amber-600 dark:text-amber-400">
            Tip: Adding a date of birth enables age-matched comparisons.
          </p>
        )}
      </div>
    );
  }

  // Build age-matched lookup by testType
  const ageBenchmarkMap = new Map<string, number>();
  if (ageData?.benchmarks) {
    for (const b of ageData.benchmarks) {
      ageBenchmarkMap.set(b.testType, b.bestValue);
    }
  }

  // Group history by testType
  const historyByType = new Map<string, typeof history>();
  for (const result of history) {
    if (!historyByType.has(result.testType)) historyByType.set(result.testType, []);
    historyByType.get(result.testType)!.push(result);
  }

  return (
    <div className="space-y-4">
      {!dateOfBirth && (
        <p className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-md">
          Add your child's date of birth to unlock age-matched benchmark comparisons.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {benchmarks.map((b) => (
          <TestCard
            key={b.testType}
            testType={b.testType}
            history={historyByType.get(b.testType) ?? []}
            benchmark={b}
            ageBestValue={ageBenchmarkMap.get(b.testType) ?? null}
            ageMatchedAge={ageData?.ageMatchedAge ?? null}
          />
        ))}
      </div>
    </div>
  );
}
